import { TFunction } from 'i18next';
import { Dayjs } from 'dayjs';
import {
  IconAlertTriangle,
  IconCar,
  IconCarCrash,
  IconClockHour4,
  IconRosetteDiscountCheck,
  IconShieldCheck,
  IconShieldSearch,
} from '@tabler/icons-react';
import { Options } from '@/components/Controls/Select';
import { RichSelect, RichSelectOption } from '@/components/Controls/RichSelect';
import { HumanModerationStatus } from '@/api/entities/moderation';
import {
  EntityFilterSelect,
  FilterDateRange,
  FilterEntity,
  FilterField,
  FilterGrid,
  withoutEmptyOption,
} from './FilterKit';

type VehicleCheckFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  humanModerationStatusOptions?: Options[];
  reportValidatedOptions?: Options[];
  groupOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  displayGroupFilter?: boolean;
  displayCompanyFilter?: boolean;
  displayBranchFilter?: boolean;
  /** Displays the "damage status" filter. */
  displayDamageStatusFilter?: boolean;
  damageStatus?: string;
  humanModerationStatus?: string;
  reportValidated?: string;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
  searchStartDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchByDamageStatus?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByHumanModerationStatus?: (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
  searchByReportValidated?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

/** Same colors as the status tags of the inspection lists. */
const PILL = {
  red: 'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
  green: 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
  orange: 'bg-orange-FF6D00/5 border-orange-FF6D00/20 text-orange-FF6D00',
};

const MODERATION_PILLS: Record<string, Partial<RichSelectOption>> = {
  [HumanModerationStatus.A_MODERER]: {
    pillClassName: PILL.red,
    icon: <IconAlertTriangle size={12} stroke={2.4} />,
  },
  [HumanModerationStatus.MODERE]: {
    pillClassName: PILL.green,
    icon: <IconShieldCheck size={12} stroke={2.4} />,
  },
};

const VALIDATION_PILLS: Record<string, Partial<RichSelectOption>> = {
  false: {
    pillClassName: PILL.orange,
    icon: <IconClockHour4 size={12} stroke={2.4} />,
  },
  true: {
    pillClassName: PILL.green,
    icon: <IconRosetteDiscountCheck size={12} stroke={2.4} />,
  },
};

const toPillOptions = (
  options: Options[] | undefined,
  pills: Record<string, Partial<RichSelectOption>>,
): RichSelectOption[] =>
  withoutEmptyOption(options ?? []).map((option) => ({
    label: String(option.label),
    value: option.value,
    ...pills[String(option.value)],
  }));

const VehicleCheckFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  humanModerationStatusOptions,
  reportValidatedOptions,
  groupOptions,
  companyOptions,
  branchOptions,
  displayGroupFilter,
  displayBranchFilter,
  displayCompanyFilter,
  displayDamageStatusFilter,
  damageStatus,
  humanModerationStatus,
  reportValidated,
  groupId,
  companyId,
  branchId,
  startDate,
  endDate,
  searchStartDate,
  searchEndDate,
  searchByDamageStatus,
  searchByHumanModerationStatus,
  searchByReportValidated,
  searchByGroup,
  searchByCompany,
  searchByBranch,
}: VehicleCheckFilterProps) => {
  return (
    <FilterGrid>
      <FilterDateRange
        label={t('common:createdBetween_feminine')}
        startDate={startDate}
        endDate={endDate}
        onStartChange={searchStartDate}
        onEndChange={searchEndDate}
        className="sm:col-span-2"
      />
      {displayDamageStatusFilter && (
        <FilterField
          icon={IconCarCrash}
          tone="orange"
          label={t('common:damageStatus')}
        >
          <RichSelect
            placeholder={t('common:allStatuses')}
            aria-label={t('common:damageStatus')}
            options={[
              {
                label: t('common:withDamages'),
                value: 'true',
                icon: <IconCarCrash size={15} className="text-orange-600" />,
              },
              {
                label: t('common:withoutDamages'),
                value: 'false',
                icon: <IconCar size={15} className="text-emerald-600" />,
              },
            ]}
            value={damageStatus || ''}
            onChange={searchByDamageStatus}
          />
        </FilterField>
      )}
      <FilterField
        icon={IconShieldSearch}
        tone="rose"
        label={t('common:moderationStatus')}
      >
        <RichSelect
          placeholder={t('common:allModerations')}
          aria-label={t('common:moderationStatus')}
          options={toPillOptions(
            humanModerationStatusOptions,
            MODERATION_PILLS,
          )}
          value={humanModerationStatus || ''}
          onChange={searchByHumanModerationStatus}
        />
      </FilterField>
      <FilterField
        icon={IconRosetteDiscountCheck}
        tone="teal"
        label={t('common:validationStatus')}
      >
        <RichSelect
          placeholder={t('common:allValidations')}
          aria-label={t('common:validationStatus')}
          options={toPillOptions(reportValidatedOptions, VALIDATION_PILLS)}
          value={reportValidated || ''}
          onChange={searchByReportValidated}
        />
      </FilterField>
      {displayGroupFilter && (
        <EntityFilterSelect
          entity={FilterEntity.GROUP}
          label={t('common:linkedToGroup')}
          options={groupOptions}
          value={groupId}
          loading={isLoadingAllGroups}
          onChange={searchByGroup}
        />
      )}
      {displayCompanyFilter && (
        <EntityFilterSelect
          entity={FilterEntity.COMPANY}
          label={t('common:linkedToCompany')}
          options={companyOptions}
          value={companyId}
          loading={isLoadingAllCompanies}
          emptyMessage={
            groupId
              ? t('common:noCompanyLinkedToThisGroup')
              : t('common:youAreNotLinkedToAnyCompany')
          }
          onChange={searchByCompany}
        />
      )}
      {displayBranchFilter && (
        <EntityFilterSelect
          entity={FilterEntity.BRANCH}
          label={t('common:linkedToBranch')}
          options={branchOptions}
          value={branchId}
          loading={isLoadingAllBranches}
          emptyMessage={
            companyId
              ? t('common:noBranchLinkedToThisCompany')
              : groupId
                ? t('common:noBranchLinkedToThisGroup')
                : t('common:youAreNotLinkedToAnyBranch')
          }
          onChange={searchByBranch}
        />
      )}
    </FilterGrid>
  );
};

export default VehicleCheckFilter;
